<template>
  <div :class="wrapperClasses" class="sd-card p-6">
    <div class="sd-chart-card__header m-0 mb-4 pb-4">
      <h3 :class="hasWarnings ? 'text-red-600' : 'text-green-600'" class="sd-chart-card__title">
        <svg v-if="hasWarnings" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
        </svg>
        <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        {{ $t('sales_dashboard.margin_warnings') }}
      </h3>
      <span v-if="hasWarnings" class="sd-badge" style="background: hsla(0, 84%, 60%, 0.15); color: hsl(0, 84%, 50%);">
        {{ lowMarginContracts.length }} {{ $t('sales_dashboard.deals') }}
      </span>
    </div>

    <!-- No warnings state -->
    <div v-if="!hasWarnings" class="text-sm text-secondary-600">
      <span class="text-green-600">✓</span> {{ $t('sales_dashboard.no_margin_warnings') }}
    </div>

    <!-- Warnings list -->
    <div v-else>
      <p class="text-sm text-secondary-600 mb-4">
        {{ $t('sales_dashboard.margin_warnings_desc') }}
      </p>

      <!-- Low margin contracts -->
      <div class="space-y-3 max-h-96 overflow-y-auto pr-1">
        <div 
          v-for="contract in lowMarginContracts" 
          :key="contract.id || contract._id"
          class="flex items-start gap-3 p-3 bg-white rounded-lg border border-red-200 hover:border-red-300 transition-all"
        >
          <div class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-red-100">
            <svg class="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          
          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between">
              <p class="font-semibold text-secondary-800 truncate">
                {{ getCustomerName(contract) }}
              </p>
              <span class="text-red-600 font-bold text-sm">
                {{ contract.marginPercent !== null ? contract.marginPercent.toFixed(0) : '-' }}%
              </span>
            </div>
            <p class="text-sm text-secondary-500 flex items-center gap-1 mt-1">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
              </svg>
              {{ getSellerName(contract) }}
            </p>
            <p v-if="contract.productSummary" class="text-xs text-secondary-400 mt-1">
              {{ contract.productSummary }}
            </p>
          </div>
          
          <div class="text-right text-sm">
            <p class="font-semibold text-secondary-700">{{ formatCurrency(getContractValue(contract)) }}</p>
            <p class="text-xs text-secondary-400">{{ formatDate(contract.startDate || contract.contract_start || contract.created_at) }}</p>
          </div>
        </div>
      </div>

      <!-- Salesperson summary -->
      <div v-if="salespersonWarnings.length > 0" class="mt-6 pt-4 border-t border-red-200">
        <h4 class="text-sm font-semibold text-secondary-700 mb-3 flex items-center gap-2">
          <svg class="w-4 h-4 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
          </svg>
          {{ $t('sales_dashboard.salesperson_warnings') }}
        </h4>
        
        <div class="space-y-2">
          <div 
            v-for="sp in salespersonWarnings" 
            :key="sp.id"
            @click="$emit('select-salesperson', sp)"
            class="flex items-center justify-between p-2 bg-white rounded-lg border border-secondary-200 cursor-pointer hover:border-securdia-blue/50 transition-all"
          >
            <div class="flex items-center gap-2">
              <span class="font-medium text-sm text-secondary-700">{{ sp.name }}</span>
              <span class="text-xs text-secondary-400">
                {{ sp.lowMarginCount }}/{{ sp.totalDeals }} {{ $t('sales_dashboard.low_margin') }}
              </span>
            </div>
            <div class="flex items-center gap-2">
              <div class="sd-progress w-16">
                <div 
                  :style="{ width: sp.lowMarginPercent + '%' }"
                  class="sd-progress__fill" style="background: hsl(0, 84%, 60%);"
                ></div>
              </div>
              <span :class="sp.avgMargin < 30 ? 'text-red-600' : 'text-yellow-600'" class="text-sm font-semibold">
                {{ sp.avgMargin.toFixed(0) }}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LowMarginWarnings',
  props: {
    contracts: {
      type: Array,
      default: () => []
    },
    salespersons: {
      type: Array,
      default: () => []
    },
    marginThreshold: {
      type: Number,
      default: 40
    }
  },
  computed: {
    lowMarginContracts() {
      if (!this.contracts || this.contracts.length === 0) return [];
      
      return this.contracts
        .map(c => {
          // Use server-provided marginPercent, fallback to local calculation
          const marginPercent = c.marginPercent !== null && c.marginPercent !== undefined 
            ? c.marginPercent 
            : this.calculateMargin(c);
          const productSummary = this.getProductSummary(c);
          
          return {
            ...c,
            marginPercent,
            productSummary
          };
        })
        .filter(c => c.marginPercent !== null && c.marginPercent < this.marginThreshold)
        .sort((a, b) => a.marginPercent - b.marginPercent)
        .slice(0, 10);
    },
    
    hasWarnings() {
      return this.lowMarginContracts.length > 0;
    },
    
    wrapperClasses() {
      return this.hasWarnings
        ? 'bg-danger-50 border border-danger-200'
        : 'bg-accent-50 border border-accent-200';
    },
    
    salespersonWarnings() {
      if (!this.contracts || this.contracts.length === 0) return [];
      
      const stats = {};
      
      this.contracts.forEach(c => {
        const sellerId = c.sellerId || c.seller_id || c.seller;
        if (!sellerId) return;
        
        // Use server-provided marginPercent, fallback to local calculation
        const margin = c.marginPercent !== null && c.marginPercent !== undefined 
          ? c.marginPercent 
          : this.calculateMargin(c);
        if (margin === null || margin <= 0) return;
        
        if (!stats[sellerId]) {
          // Get seller name from contract (server now provides it) or lookup
          const sellerName = c.seller || this.salespersons.find(s => s._id === sellerId || s.id === sellerId)?.name;
          stats[sellerId] = {
            id: sellerId,
            name: sellerName || this.$t('sales_dashboard.unknown_seller'),
            lowMarginCount: 0,
            totalDeals: 0,
            totalMargin: 0
          };
        }
        
        stats[sellerId].totalDeals++;
        stats[sellerId].totalMargin += margin;
        
        if (margin < this.marginThreshold) {
          stats[sellerId].lowMarginCount++;
        }
      });
      
      return Object.values(stats)
        .map(sp => ({
          ...sp,
          avgMargin: sp.totalDeals > 0 ? sp.totalMargin / sp.totalDeals : 0,
          lowMarginPercent: sp.totalDeals > 0 ? (sp.lowMarginCount / sp.totalDeals) * 100 : 0
        }))
        .filter(sp => sp.lowMarginCount > 0)
        .sort((a, b) => b.lowMarginPercent - a.lowMarginPercent)
        .slice(0, 5);
    }
  },
  methods: {
    calculateMargin(contract) {
      // If margin is stored directly
      if (contract.margin_percent) {
        return parseFloat(contract.margin_percent);
      }
      
      // Calculate from values if available
      const revenue = this.getContractValue(contract);
      const cost = this.parseMoneyValue(contract.total_cost) || 
                   this.parseMoneyValue(contract.purchase_cost) || 0;
      
      if (revenue > 0 && cost > 0) {
        return ((revenue - cost) / revenue) * 100;
      }
      
      return 0;
    },
    
    getContractValue(contract) {
      // Support both camelCase (new API) and snake_case (legacy)
      const rentalCost = this.parseMoneyValue(contract.monthlyValue || contract.rental_month_cost);
      const cashSum = this.parseMoneyValue(contract.cashValue || contract.cash_sum);
      const category = contract.category || contract.agreement_category;
      const period = contract.contractPeriod || contract.contract_period || 36;
      
      if (category === 'cash_services' || cashSum > 0) {
        return cashSum;
      }
      
      return rentalCost * parseInt(period);
    },
    
    parseMoneyValue(value) {
      if (!value) return 0;
      if (typeof value === 'number') return value;
      const cleaned = String(value).replace(/[^\d.-]/g, '');
      return parseFloat(cleaned) || 0;
    },
    
    getCustomerName(contract) {
      // Support both camelCase (new API) and snake_case (legacy)
      return contract.customerName || contract.customer_name || contract.customer?.name || this.$t('sales_dashboard.unknown_customer');
    },
    
    getSellerName(contract) {
      // New API provides seller as name directly
      if (contract.seller && typeof contract.seller === 'string' && contract.seller !== contract.sellerId) {
        return contract.seller;
      }
      if (contract.seller_name) return contract.seller_name;
      
      const sellerId = contract.sellerId || contract.seller_id || contract.seller;
      const seller = this.salespersons.find(s => s._id === sellerId || s.id === sellerId);
      return seller?.name || this.$t('sales_dashboard.unknown_seller');
    },
    
    getProductSummary(contract) {
      const products = contract.products || [];
      if (products.length === 0) return '';
      
      const productNames = products.slice(0, 2).map(p => 
        `${p.name || p.product_name || 'Produkt'} (${p.qty || 1}st)`
      );
      
      let summary = productNames.join(', ');
      if (products.length > 2) {
        summary += ` +${products.length - 2}`;
      }
      
      return summary;
    },
    
    formatCurrency(value) {
      return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: 'SEK',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString('sv-SE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  }
};
</script>
